import * as React from 'react';
import { flexRender } from '@tanstack/react-table';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useLegacyTable,
} from '@tanstack/react-table/legacy';
import { Search } from 'lucide-react';
import { Input } from 'components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { DataTablePagination } from './DataTablePagination';
import { cn } from 'lib/utils';
import './data-table.css';

function globalTextFilter(row, _columnId, filterValue) {
  const q = String(filterValue ?? '')
    .toLowerCase()
    .trim();
  if (!q) return true;
  return Object.values(row.original || {}).some((value) =>
    String(value ?? '')
      .toLowerCase()
      .includes(q)
  );
}

/**
 * Reusable shadcn/TanStack data table — dashboard-01 style.
 */
export function DataTable({
  columns,
  data,
  searchPlaceholder = 'Filter…',
  toolbarActions = null,
  meta,
  initialPageSize = 10,
  className,
}) {
  const [sorting, setSorting] = React.useState([]);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useLegacyTable({
    data,
    columns,
    meta,
    enableRowSelection: true,
    getRowId: (row, index) => String(row.$id ?? row.id ?? index),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    globalFilterFn: globalTextFilter,
    initialState: {
      pagination: { pageSize: initialPageSize },
    },
    state: {
      sorting,
      globalFilter,
      rowSelection,
    },
  });

  return (
    <div className={cn('data-table flex flex-col gap-4', className)}>
      <div className="flex items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={globalFilter ?? ''}
            onChange={(event) => setGlobalFilter(event.target.value)}
            className="data-table__search h-9 pl-9"
            aria-label={searchPlaceholder}
          />
        </div>
        {typeof toolbarActions === 'function' ? toolbarActions(table) : toolbarActions}
      </div>

      <div className="data-table__shell overflow-hidden rounded-lg border border-solid border-border">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-muted">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <DataTablePagination table={table} />
    </div>
  );
}
